document.addEventListener('alpine:init', () => {
  Alpine.data('productCarousel', (images, variants) => ({
    images: images || [],
    variants: variants || [],
    selectedImageIndex: 0,
    isScrolling: false,
    // Thumbnail pagination state
    currentThumbnailPage: 0,
    thumbnailsPerPage: 6,
    isScrollingThumbnails: false,
    // Layout transition state
    wasMobile: window.innerWidth < 1024,
    isLayoutTransitioning: false,
    scrollTimeout: null,

    init() {
      // Watch for currentVariant changes from productForm
      this.$watch('$store.productForm.currentVariant', (variant, oldVariant) => {
        if (variant && variant.featured_image) {
          this.selectImageByVariant(variant.featured_image.id);
        }
      });

      // Watch for selectedColor changes when no complete variant is selected
      this.$watch('$store.productForm.selectedColor', (selectedColor, oldColor) => {
        const currentVariant = this.$store.productForm.currentVariant;
        
        // Only respond to color changes if no complete variant is selected
        if (selectedColor && !currentVariant) {
          this.selectImageByColor(selectedColor);
        }
      });

      // Also check for initial variant on next tick
      this.$nextTick(() => {
        const currentVariant = this.$store.productForm.currentVariant;
        if (currentVariant && currentVariant.featured_image) {
          this.selectImageByVariant(currentVariant.featured_image.id);
        }
      });

      // Add resize listener to detect mobile/desktop transitions
      window.addEventListener('resize', this.handleResize.bind(this));

      // Debug: Check DOM elements after initialization
      this.$nextTick(() => {
        const thumbnailCarousel = this.$refs.thumbnailCarousel;
        const thumbnailPages = thumbnailCarousel?.querySelectorAll('.c-product-carousel__thumbnail-page');
        const thumbnailButtons = thumbnailCarousel?.querySelectorAll('.c-product-carousel__thumbnail');
        
        if (thumbnailPages) {
          thumbnailPages.forEach((page, index) => {
            const buttonsInPage = page.querySelectorAll('.c-product-carousel__thumbnail');
          });
        }
      });
    },

    destroy() {
      // Clean up resize listener
      window.removeEventListener('resize', this.handleResize.bind(this));
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
    },

    selectImage(index) {
      if (index >= 0 && index < this.images.length) {
        this.selectedImageIndex = index;
        this.scrollToImage(index);
      }
    },

    selectImageByVariant(imageId) {
      const imageIndex = this.images.findIndex(image => image.id === imageId);
      
      if (imageIndex !== -1) {
        this.selectedImageIndex = imageIndex;
        this.scrollToImage(imageIndex);
      }
    },

    selectImageByColor(selectedColor) {
      // Find the first available variant with the selected color
      const variantWithColor = this.variants.find(variant => {
        // Check if this variant has the selected color
        // Variants store options in order, and we need to find the color option
        return variant.options.some(option => option === selectedColor) && variant.available;
      });
      
      if (variantWithColor && variantWithColor.featured_image) {
        this.selectImageByVariant(variantWithColor.featured_image.id);
      }
    },

    scrollToImage(index) {
      const carousel = this.$refs.mainCarousel;
      if (carousel && index >= 0 && index < this.images.length) {
        const imageElements = carousel.querySelectorAll('.c-product-carousel__main-image');
        const targetImage = imageElements[index];
        
        if (targetImage) {
          if (this.isMobile()) {
            // Mobile: Horizontal scroll
            const targetScrollLeft = targetImage.offsetLeft;
            carousel.scrollTo({
              left: targetScrollLeft,
              behavior: 'smooth'
            });
          } else {
            // Desktop: Vertical scroll
            const targetScrollTop = targetImage.offsetTop;
            carousel.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            });
          }
        }
      }
    },

    updateActiveImageOnScroll() {
      if (this.isScrolling || this.isLayoutTransitioning) return;
      
      const carousel = this.$refs.mainCarousel;
      if (!carousel) return;
      
      // Clear any existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // Add small delay to prevent immediate override of variant selections
      this.scrollTimeout = setTimeout(() => {
        const imageElements = carousel.querySelectorAll('.c-product-carousel__main-image');
        
        // Find which image is most visible in the viewport
        let mostVisibleIndex = 0;
        let maxVisibleArea = 0;
        
        imageElements.forEach((imageElement, index) => {
          const rect = imageElement.getBoundingClientRect();
          const carouselRect = carousel.getBoundingClientRect();
          
          let visibleArea = 0;
          
          if (this.isMobile()) {
            // Mobile: Calculate horizontal visibility
            const visibleLeft = Math.max(rect.left, carouselRect.left);
            const visibleRight = Math.min(rect.right, carouselRect.right);
            const visibleWidth = Math.max(0, visibleRight - visibleLeft);
            visibleArea = visibleWidth;
          } else {
            // Desktop: Calculate vertical visibility
            const visibleTop = Math.max(rect.top, carouselRect.top);
            const visibleBottom = Math.min(rect.bottom, carouselRect.bottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            visibleArea = visibleHeight;
          }
          
          if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea;
            mostVisibleIndex = index;
          }
        });
        
        if (mostVisibleIndex !== this.selectedImageIndex) {
          this.selectedImageIndex = mostVisibleIndex;
        }
      }, 150); // Small delay to prevent conflicts with variant selection
    },

    // Thumbnail pagination methods
    nextThumbnailPage() {
      if (this.currentThumbnailPage < this.totalThumbnailPages - 1) {
        this.goToThumbnailPage(this.currentThumbnailPage + 1);
      }
    },

    prevThumbnailPage() {
      if (this.currentThumbnailPage > 0) {
        this.goToThumbnailPage(this.currentThumbnailPage - 1);
      }
    },

    goToThumbnailPage(pageIndex) {
      if (pageIndex >= 0 && pageIndex < this.totalThumbnailPages) {
        this.currentThumbnailPage = pageIndex;
        this.scrollToThumbnailPage(pageIndex);
      }
    },

    scrollToThumbnailPage(pageIndex) {
      const carousel = this.$refs.thumbnailCarousel;
      if (carousel && pageIndex >= 0 && pageIndex < this.totalThumbnailPages) {
        this.isScrollingThumbnails = true;
        
        // Calculate scroll position: pageIndex * container width
        const scrollPosition = pageIndex * carousel.clientWidth;
        
        carousel.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
        
        // Reset scrolling flag after animation
        setTimeout(() => {
          this.isScrollingThumbnails = false;
        }, 300);
      }
    },

    updatePaginationOnScroll() {
      if (this.isScrollingThumbnails) return;
      
      const carousel = this.$refs.thumbnailCarousel;
      if (!carousel) return;
      
      const scrollLeft = carousel.scrollLeft;
      const pageWidth = carousel.clientWidth;
      
      // More accurate page detection with tolerance
      let currentPage = Math.round(scrollLeft / pageWidth);
      currentPage = Math.max(0, Math.min(currentPage, this.totalThumbnailPages - 1));
      
      // Add tolerance for slight scroll variations (within 10% of page width)
      const tolerance = pageWidth * 0.1;
      const pagePosition = scrollLeft / pageWidth;
      
      // Snap to page if we're close enough
      if (Math.abs(pagePosition - Math.round(pagePosition)) < tolerance / pageWidth) {
        currentPage = Math.round(pagePosition);
      }
      
      if (currentPage !== this.currentThumbnailPage) {
        this.currentThumbnailPage = currentPage;
      }
    },

    // Mobile navigation methods
    nextImage() {
      if (this.selectedImageIndex < this.images.length - 1) {
        this.selectImage(this.selectedImageIndex + 1);
      }
    },

    prevImage() {
      if (this.selectedImageIndex > 0) {
        this.selectImage(this.selectedImageIndex - 1);
      }
    },

    // Layout transition methods
    handleResize() {
      const isMobile = this.isMobile();
      
      // Check if we've crossed the mobile/desktop breakpoint
      if (this.wasMobile !== isMobile) {
        this.wasMobile = isMobile;
        this.isLayoutTransitioning = true;
        
        // Re-sync with current variant after layout change
        this.$nextTick(() => {
          this.resyncWithCurrentVariant();
          
          // Reset transition flag after a brief delay
          setTimeout(() => {
            this.isLayoutTransitioning = false;
          }, 500);
        });
      }
    },

    resyncWithCurrentVariant() {
      const currentVariant = this.$store.productForm.currentVariant;
      if (currentVariant && currentVariant.featured_image) {
        this.selectImageByVariant(currentVariant.featured_image.id);
      }
    },

    // Helper methods
    isMobile() {
      return window.innerWidth < 1024;
    },

    // Computed properties
    get totalThumbnailPages() {
      return Math.ceil(this.images.length / this.thumbnailsPerPage);
    },

    get currentImage() {
      return this.images[this.selectedImageIndex] || this.images[0];
    }
  }));
});